import { useState } from "react";
import { motion } from "framer-motion";
import {
  ShieldCheck, ShieldQuestion, Plus, Check, Landmark, Info, ExternalLink, CalendarClock,
  BadgeCheck, UserCheck, SlidersHorizontal, TrendingUp, Wrench, Scale,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { COMPLIANCE_STATUS, COMPLIANCE_BAND, URGENCY, VERIFIED_BY, REMINDER_TIER } from "@/lib/constants";

const fade = { initial: { opacity: 0, y: 12 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.4 } };
const CONSEQUENCE = { high: "text-red-700", medium: "text-amber-700", low: "text-slate-600" };
const urgencyToPriority = { CRITICAL: "Critical", HIGH: "High", MEDIUM: "Medium", LOW: "Low", INFORMATIONAL: "Low" };
const CONFIRM_STATUSES = ["ACTIVE", "EXPIRED", "SUSPENDED", "REVOKED", "PENDING", "NOT_FOUND", "NOT_APPLICABLE"];

function money(sym, band) {
  if (!band) return null;
  const [lo, hi] = band;
  if ((lo || 0) === (hi || 0)) return `${sym}${(lo || 0).toLocaleString()}`;
  return `${sym}${(lo || 0).toLocaleString()}–${sym}${(hi || 0).toLocaleString()}`;
}

export function ComplianceScoreBadge({ compliance, testid = "compliance-score" }) {
  if (!compliance?.available) return null;
  const band = COMPLIANCE_BAND[compliance.band] || COMPLIANCE_BAND.ATTENTION;
  return (
    <div data-testid={testid} className="rounded-xl border border-slate-200 bg-white px-5 py-4 flex items-center gap-4">
      <div className="relative w-16 h-16 rounded-full grid place-items-center shrink-0"
        style={{ background: `conic-gradient(${band.ring} ${compliance.score * 3.6}deg, #e2e8f0 0deg)` }}>
        <div className="w-12 h-12 rounded-full bg-white grid place-items-center">
          <span className={`font-heading font-bold text-lg ${band.cls}`}>{compliance.score}</span>
        </div>
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold">Compliance / Licensing</div>
        <div className={`font-heading font-bold ${band.cls}`}>{band.label}</div>
        <div className="text-[11px] text-slate-400">Based on publicly verifiable / self-reported info</div>
      </div>
    </div>
  );
}

/* Three SEPARATE financial categories — never combined into one number. */
export function ImpactOverview({ financial, compliance, sym = "$" }) {
  const finAvail = financial?.available;
  const opp = finAvail ? money(sym, financial.totals?.adjusted_annual) : null;
  const exp = compliance?.available ? money(sym, compliance.exposure_total?.annual) : null;
  const rem = compliance?.available && compliance.remediation_total
    ? `${sym}${Number(compliance.remediation_total).toLocaleString()}` : null;
  if (!opp && !exp && !compliance?.available) return null;
  return (
    <div className="grid sm:grid-cols-3 gap-4" data-testid="impact-overview">
      <div className="rounded-xl border border-emerald-200 bg-emerald-50/70 p-5" data-testid="impact-opportunity">
        <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-emerald-700"><TrendingUp className="w-4 h-4" /> Financial Opportunity</div>
        <div className="font-heading text-2xl font-bold text-emerald-800 mt-1">{opp || "—"}</div>
        <div className="text-[11px] text-emerald-700/70 mt-0.5">Estimated recoverable revenue / yr · MODELED</div>
      </div>
      <div className="rounded-xl border border-red-200 bg-red-50/70 p-5" data-testid="impact-exposure">
        <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-red-700"><Scale className="w-4 h-4" /> Compliance / Risk Exposure</div>
        <div className="font-heading text-2xl font-bold text-red-800 mt-1">{exp || "Not modeled yet"}</div>
        <div className="text-[11px] text-red-700/70 mt-0.5">{exp ? "Modeled risk while unresolved / yr — not an actual loss" : "Modeled only after a problem is confirmed"}</div>
      </div>
      <div className="rounded-xl border border-slate-200 bg-slate-50 p-5" data-testid="impact-remediation">
        <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-slate-600"><Wrench className="w-4 h-4" /> Remediation Cost</div>
        <div className="font-heading text-2xl font-bold text-slate-800 mt-1">{rem || "Not verified"}</div>
        <div className="text-[11px] text-slate-500 mt-0.5">{rem ? "Estimated cost to correct / renew" : "Confirm fees with the issuing authority"}</div>
      </div>
    </div>
  );
}

function StatusPill({ status }) {
  const s = COMPLIANCE_STATUS[status] || COMPLIANCE_STATUS.UNABLE_TO_VERIFY;
  return <span data-testid={`compliance-status-${status}`} className={`rounded-full border px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide ${s.cls}`}>{s.label}</span>;
}

function VerifiedByPill({ verifiedBy }) {
  const v = VERIFIED_BY[verifiedBy] || VERIFIED_BY.UNVERIFIED;
  const Icon = verifiedBy === "PUBLIC_SOURCE" ? BadgeCheck : verifiedBy === "ADMIN_CONFIRMED" ? UserCheck : Info;
  return (
    <span data-testid={`compliance-verifiedby-${verifiedBy || "UNVERIFIED"}`}
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${v.cls}`}>
      <Icon className="w-3 h-3" /> {v.label}
    </span>
  );
}

function ReminderBadge({ reminder }) {
  if (!reminder) return null;
  const r = REMINDER_TIER[reminder.tier] || REMINDER_TIER.ACTIVE;
  return (
    <span data-testid={`compliance-reminder-${reminder.tier}`}
      className={`inline-flex items-center gap-1 rounded px-2 py-0.5 border text-[10px] font-bold uppercase tracking-wide ${r.cls}`}>
      <CalendarClock className="w-3 h-3" /> {reminder.label}
    </span>
  );
}

function OverrideDialog({ item, open, onOpenChange, onOverride }) {
  const ac = item.admin_confirmed || {};
  const [status, setStatus] = useState(ac.status || item.status || "ACTIVE");
  const [expiration, setExpiration] = useState(ac.expiration || item.expiration || "");
  const [licenseNumber, setLicenseNumber] = useState(ac.license_number || item.license_number || "");
  const [cost, setCost] = useState(ac.cost_to_correct ?? "");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const run = async (action) => {
    if (action === "confirm" && !reason.trim()) return;
    setBusy(true);
    try {
      await onOverride({
        code: item.req_code, action, status,
        expiration: expiration || null, license_number: licenseNumber || null,
        cost_to_correct: cost === "" ? null : Number(cost), reason,
      });
      onOpenChange(false);
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="compliance-override-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle>Confirm license — {item.name}</DialogTitle>
          <DialogDescription>
            Record a verified status for this license. This is stored as <b>ADMIN CONFIRMED</b> and is never
            presented as a government-verified result. The original scanned finding & evidence are preserved.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-3">
          <div>
            <Label className="text-xs">Confirmed status</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger data-testid="compliance-override-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CONFIRM_STATUSES.map((s) => <SelectItem key={s} value={s} data-testid={`compliance-override-status-${s}`}>{COMPLIANCE_STATUS[s]?.label || s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Expiration date</Label>
              <Input data-testid="compliance-override-expiration" type="date" value={expiration || ""} onChange={(e) => setExpiration(e.target.value)} />
            </div>
            <div>
              <Label className="text-xs">License # (optional)</Label>
              <Input data-testid="compliance-override-license" value={licenseNumber} onChange={(e) => setLicenseNumber(e.target.value)} placeholder="e.g. FS-12345" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Cost to correct / renew (optional)</Label>
            <Input data-testid="compliance-override-cost" type="number" min="0" value={cost} onChange={(e) => setCost(e.target.value)} placeholder="Official fee only — do not guess" />
          </div>
          <div>
            <Label className="text-xs">Reason (required)</Label>
            <Textarea data-testid="compliance-override-reason" rows={2} value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Confirmed active on the state licensing portal on 2026-06-01." />
          </div>
        </div>
        <DialogFooter className="flex-col sm:flex-row gap-2">
          {item.admin_confirmed && (
            <Button data-testid="compliance-override-clear" variant="outline" disabled={busy} onClick={() => run("clear")}
              className="mr-auto text-slate-600">Clear confirmation</Button>
          )}
          <Button data-testid="compliance-override-apply" disabled={busy || !reason.trim()} onClick={() => run("confirm")}
            className="bg-slate-900 hover:bg-slate-800 text-white">{busy ? "Saving…" : "Confirm status"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function LicenseCard({ item, onFix, added, index, admin, onOverride }) {
  const [ovOpen, setOvOpen] = useState(false);
  const u = URGENCY[item.urgency];
  const isAdded = added[item.code];
  const problem = item.is_problem;
  const exp = item.exposure;
  return (
    <motion.div {...fade} data-testid={`compliance-card-${index}`}
      className={`rounded-xl border bg-white p-5 shadow-sm ${problem ? "border-red-300 ring-1 ring-red-100" : "border-slate-200"}`}>
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5 text-slate-400 text-[11px] font-semibold">
            <Landmark className="w-3.5 h-3.5" /> {item.jurisdiction} · {item.license_type_label}
          </div>
          <h4 className="font-heading font-semibold text-slate-900 leading-snug">{item.name}</h4>
          <p className="text-[11px] text-slate-400 mt-0.5">{item.authority}</p>
        </div>
        <StatusPill status={item.status} />
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px]">
        {u && <span className={`rounded px-2 py-0.5 border font-bold uppercase tracking-wide ${u.cls}`}>{u.label}</span>}
        <VerifiedByPill verifiedBy={item.verified_by} />
        <ReminderBadge reminder={item.reminder} />
        <span className="rounded px-2 py-0.5 bg-slate-100 text-slate-500 font-semibold">Confidence: {item.confidence}</span>
        <span className={`rounded px-2 py-0.5 bg-slate-100 font-semibold ${CONSEQUENCE[item.consequence]}`}>{item.consequence} consequence</span>
      </div>

      {item.applicability === "CONDITIONAL" && item.condition && (
        <p className="mt-2 text-xs text-slate-500"><span className="font-semibold">Applies:</span> {item.condition}.</p>
      )}
      {item.why_it_matters && <p className="mt-2 text-sm text-slate-600">{item.why_it_matters}</p>}
      <p className="mt-2 text-sm text-slate-700"><span className="font-semibold">What to do:</span> {item.recommended_action}</p>

      {/* Provenance line: source, url, verification date, license number, expiration */}
      <div className="mt-3 rounded-lg bg-slate-50 border border-slate-200 p-2.5 text-[11px] text-slate-500 space-y-0.5" data-testid={`compliance-provenance-${index}`}>
        <div><span className="font-semibold text-slate-600">Source:</span> {item.source_name}{item.source_url && (
          <a href={item.source_url} target="_blank" rel="noreferrer" className="text-indigo-600 hover:underline inline-flex items-center gap-0.5 ml-1">reference <ExternalLink className="w-3 h-3" /></a>
        )}</div>
        {item.verification_date && <div><span className="font-semibold text-slate-600">Verification date:</span> {String(item.verification_date).slice(0, 10)}</div>}
        {item.license_number && <div><span className="font-semibold text-slate-600">License #:</span> {item.license_number}</div>}
        {item.expiration && <div><span className="font-semibold text-slate-600">Expiration:</span> {String(item.expiration).slice(0, 10)}</div>}
        <div><span className="font-semibold text-slate-600">Evidence:</span> {item.evidence}</div>
      </div>

      {/* Financial: exposure (modeled) vs cost to correct — kept distinct */}
      <div className="mt-3 grid sm:grid-cols-2 gap-2">
        <div className="rounded-lg bg-red-50/60 border border-red-100 p-2.5">
          <div className="text-[10px] uppercase tracking-wide text-red-700/80 font-semibold">Potential exposure · MODELED</div>
          {exp ? (
            <div className="text-xs text-red-800 font-semibold" data-testid={`compliance-exposure-${index}`}>{money("$", exp.annual)}/yr <span className="font-normal text-red-700/70">({money("$", exp.monthly)}/mo)</span></div>
          ) : (
            <div className="text-xs text-slate-500">{item.potential_exposure_note}</div>
          )}
          {exp && <div className="text-[10px] text-red-700/60 mt-0.5">Not an actual loss or penalty</div>}
        </div>
        <div className="rounded-lg bg-slate-50 border border-slate-200 p-2.5">
          <div className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold">Cost to correct</div>
          <div className="text-xs text-slate-600" data-testid={`compliance-cost-${index}`}>{item.cost_to_correct ? `$${Number(item.cost_to_correct).toLocaleString()}` : item.cost_note}</div>
        </div>
      </div>

      {item.admin_confirmed && (
        <div className="mt-3 rounded-lg bg-indigo-50/70 border border-indigo-100 p-2.5 text-[11px]" data-testid={`compliance-adminnote-${index}`}>
          <p className="font-semibold text-indigo-800">Admin confirmed: {item.admin_confirmed.status_label}{item.admin_confirmed.expiration ? ` · expires ${String(item.admin_confirmed.expiration).slice(0, 10)}` : ""}</p>
          {item.admin_confirmed.reason && <p className="text-indigo-700/80 mt-0.5">Reason: {item.admin_confirmed.reason}</p>}
          <p className="text-indigo-700/60 mt-0.5">This is an administrator confirmation, not a government-verified result.</p>
        </div>
      )}

      <div className="mt-3 flex items-center justify-between gap-2 flex-wrap">
        <span className="text-[10px] text-slate-400">{item.source}</span>
        <div className="flex items-center gap-2 ml-auto">
          {admin && (
            <button data-testid={`compliance-confirm-${item.req_code}`} onClick={() => setOvOpen(true)}
              className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-slate-800">
              <SlidersHorizontal className="w-3.5 h-3.5" /> Confirm status
            </button>
          )}
          {onFix && (
            <button data-testid={`compliance-fix-${item.req_code}`} onClick={() => onFix({
              code: item.code, problem: `${item.name} — ${item.status_label}`, why_it_matters: item.why_it_matters,
              recommended_action: item.recommended_action, evidence: item.source,
              priority: urgencyToPriority[item.urgency] || "High", urgency: item.urgency,
              impact_type: "COMPLIANCE_RISK", impact_note: item.potential_exposure_note,
              impact_monthly: exp?.monthly || null, impact_annual: exp?.annual || null,
            })} disabled={isAdded}
              className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${isAdded ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-slate-900 text-white hover:bg-slate-800"}`}>
              {isAdded ? <><Check className="w-3.5 h-3.5" /> In Fix Plan</> : <><Plus className="w-3.5 h-3.5" /> {problem ? "Fix This" : "Add to Fix Plan"}</>}
            </button>
          )}
        </div>
      </div>
      {admin && <OverrideDialog item={item} open={ovOpen} onOpenChange={setOvOpen} onOverride={onOverride} />}
    </motion.div>
  );
}

function DashCount({ label, value, tone = "text-slate-800", testid }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3 text-center" data-testid={testid}>
      <div className={`font-heading text-2xl font-bold leading-none ${tone}`}>{value ?? 0}</div>
      <div className="text-[10px] uppercase tracking-wide text-slate-400 mt-1 leading-tight">{label}</div>
    </div>
  );
}

export function ComplianceSection({ compliance, onFix, added = {}, admin = false, onOverride }) {
  if (!compliance) return null;
  const band = COMPLIANCE_BAND[compliance.band] || COMPLIANCE_BAND.ATTENTION;
  const sm = compliance.summary || {};

  return (
    <motion.section {...fade} className="space-y-5" data-testid="compliance-section">
      {!compliance.available ? (
        <div data-testid="compliance-unavailable" className="rounded-xl border border-slate-200 bg-white p-6">
          <p className="text-sm text-slate-600">{compliance.message || "Compliance scanning is unavailable."}</p>
          <p className="text-xs text-slate-400 mt-2">{compliance.disclaimer}</p>
        </div>
      ) : (
        <>
          {/* Score + verification-state banner */}
          <div className="grid md:grid-cols-[auto_1fr] gap-4 items-stretch">
            <div className="rounded-2xl border border-slate-200 bg-white p-6 flex items-center gap-5" data-testid="compliance-score-card">
              <div className="relative w-24 h-24 rounded-full grid place-items-center shrink-0"
                style={{ background: `conic-gradient(${band.ring} ${compliance.score * 3.6}deg, #e2e8f0 0deg)` }}>
                <div className="w-[76px] h-[76px] rounded-full bg-white grid place-items-center flex-col">
                  <span className={`font-heading font-bold text-2xl leading-none ${band.cls}`}>{compliance.score}</span>
                  <span className="text-[10px] text-slate-400">/ 100</span>
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold">Overall compliance status</div>
                <div className={`font-heading text-xl font-bold ${band.cls}`}>{compliance.band_label}</div>
                <div className="text-xs text-slate-500 mt-1">{compliance.requirements_identified} potentially-applicable requirements · {compliance.jurisdiction}</div>
              </div>
            </div>
            <div className="rounded-2xl border border-amber-200 bg-amber-50/60 p-5 flex items-start gap-3">
              <ShieldQuestion className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
              <div>
                <p className="text-sm font-semibold text-amber-900">{compliance.verifier_configured ? "Verification source connected" : "Live verification not configured"}</p>
                <p className="text-xs text-amber-800/80 mt-0.5">
                  {compliance.verifier_configured
                    ? "Statuses are checked against an authoritative source where available. Items still marked unable-to-verify need manual confirmation."
                    : <>No official license-verification source is connected, so statuses show <b>Unable to verify</b>. These are general requirements for your business type &amp; location — confirm each with the issuing authority. “Not found” does not mean “unlicensed”. Nothing here is fabricated.</>}
                </p>
                <p className="text-[11px] text-amber-700/70 mt-1.5">{compliance.basis}</p>
              </div>
            </div>
          </div>

          {/* Compliance dashboard counts */}
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-3" data-testid="compliance-dashboard">
            <DashCount label="Active" value={sm.active} tone="text-emerald-700" testid="compliance-count-active" />
            <DashCount label="Expiring soon" value={sm.expiring_soon} tone="text-amber-700" testid="compliance-count-expiring" />
            <DashCount label="Expired" value={sm.expired} tone="text-red-700" testid="compliance-count-expired" />
            <DashCount label="Not found" value={sm.not_found} tone="text-amber-700" testid="compliance-count-notfound" />
            <DashCount label="Unable to verify" value={sm.unable_to_verify} tone="text-slate-600" testid="compliance-count-unverified" />
            <DashCount label="Confirmed problems" value={sm.confirmed_problems} tone="text-red-700" testid="compliance-count-problems" />
          </div>

          {/* License cards */}
          <div className="grid lg:grid-cols-2 gap-4">
            {(compliance.items || []).map((item, i) => (
              <LicenseCard key={item.code} item={item} onFix={onFix} added={added} index={i} admin={admin} onOverride={onOverride} />
            ))}
          </div>

          <p className="text-xs text-slate-400 leading-relaxed border-t border-slate-200 pt-4 flex items-start gap-2" data-testid="compliance-disclaimer">
            <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" /> {compliance.disclaimer}
          </p>
        </>
      )}
    </motion.section>
  );
}
